(function (global, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else global.AskSeyuneRequest = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var MAX_MESSAGES = 12;
  var MAX_MESSAGE_CHARACTERS = 500;
  var MAX_TURNSTILE_TOKEN_CHARACTERS = 2048;
  var MAX_SOURCE_TITLE_CHARACTERS = 160;
  var MAX_SOURCE_URL_CHARACTERS = 2048;
  var DEFAULT_TIMEOUT_MS = 22000;

  function failureClassForCode(code) {
    if (code === 'turnstile_rejected' || code === 'origin_forbidden' || String(code).indexOf('turnstile_') === 0) {
      return 'verification_failed';
    }
    if (code === 'configuration_error' || code === 'invalid_ask_input' || code === 'invalid_mock_ask_input' || code === 'rate_limited') {
      return 'request_invalid';
    }
    if (code === 'persistence_failed' || code === 'conversation_log_unavailable') return 'persistence_failed';
    return 'generation_failed';
  }

  function isSafeHttpErrorCode(code) {
    return code === 'configuration_error' ||
      code === 'origin_forbidden' ||
      code === 'invalid_ask_input' ||
      code === 'invalid_mock_ask_input' ||
      code === 'rate_limited' ||
      code === 'turnstile_rejected';
  }

  function safeError(code) {
    var error = new Error(code);
    error.code = code;
    error.failureClass = failureClassForCode(code);
    return error;
  }

  function clampText(value) {
    return Array.from(String(value)).slice(0, MAX_MESSAGE_CHARACTERS).join('');
  }

  function validateMessages(messages) {
    if (!Array.isArray(messages) || messages.length === 0 || messages.length > MAX_MESSAGES) {
      throw safeError('invalid_messages');
    }
    var copy = messages.map(function (message) {
      if (!message || (message.role !== 'user' && message.role !== 'assistant') || typeof message.content !== 'string') {
        throw safeError('invalid_message');
      }
      if (!message.content.trim() || Array.from(message.content).length > MAX_MESSAGE_CHARACTERS) {
        throw safeError('invalid_message_length');
      }
      return { role: message.role, content: message.content };
    });
    if (copy[copy.length - 1].role !== 'user') throw safeError('invalid_message_order');
    return copy;
  }

  function validateProductContext(context) {
    if (context === null || context === undefined) return null;
    if (typeof context !== 'object' || Array.isArray(context) ||
      typeof context.handle !== 'string' || typeof context.title !== 'string' ||
      !context.handle.trim() || !context.title.trim() ||
      Array.from(context.handle).length > 100 ||
      Array.from(context.title).length > 255) {
      throw safeError('invalid_product_context');
    }
    var keys = Object.keys(context);
    if (keys.some(function (key) { return key !== 'handle' && key !== 'title'; })) {
      throw safeError('invalid_product_context');
    }
    return {
      handle: context.handle.trim(),
      title: context.title.trim()
    };
  }
  function validateConversationId(value) {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
      throw safeError('invalid_conversation_id');
    }
    return value.toLowerCase();
  }

  function validateSource(event) {
    if (Object.keys(event).some(function (key) { return key !== 'type' && key !== 'title' && key !== 'url'; }) ||
      typeof event.title !== 'string' || !event.title.trim() ||
      Array.from(event.title).length > MAX_SOURCE_TITLE_CHARACTERS ||
      typeof event.url !== 'string' || !event.url.trim() ||
      Array.from(event.url).length > MAX_SOURCE_URL_CHARACTERS) {
      throw safeError('invalid_source_event');
    }
    var parsed;
    try { parsed = new URL(event.url); } catch (error) { throw safeError('invalid_source_event'); }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') throw safeError('invalid_source_event');
    return { title: event.title.trim(), url: parsed.href };
  }

  function parseDataBlock(block) {
    var lines = block.split(/\r?\n/);
    var data = [];
    for (var i = 0; i < lines.length; i += 1) {
      var line = lines[i];
      if (!line || line.indexOf(':') === 0) continue;
      if (line.indexOf('data:') === 0) data.push(line.slice(5).replace(/^\s/, ''));
      else throw safeError('malformed_sse');
    }
    if (data.length === 0) return null;
    try {
      return JSON.parse(data.join('\n'));
    } catch (error) {
      throw safeError('malformed_sse_json');
    }
  }

  function validateRoutineResult(result) {
    var allowedReasons = {
      provider_error: true,
      provider_timeout: true,
      provider_output_limit: true,
      provider_invalid_json: true,
      provider_refusal: true,
      provider_network_error: true,
      budget_limit: true,
      safety_substitution: true,
      catalogue_unavailable: true
    };
    if (!result || typeof result !== 'object' ||
      result.schemaVersion !== 'find_my_routine.v1' ||
      (result.routineId !== 'sensitive-redness-v1' && result.routineId !== 'dry-dehydrated-v1') ||
      typeof result.intro !== 'string' || result.intro.length > 650 ||
      (result.source !== 'ai' && result.source !== 'deterministic_fallback') ||
      (result.fallbackReason !== null && !allowedReasons[result.fallbackReason]) ||
      result.currency !== 'CAD' || !Array.isArray(result.products) || result.products.length !== 3 ||
      !Number.isInteger(result.totalPriceCents) || result.totalPriceCents < 0) {
      throw safeError('invalid_routine_result');
    }
    var slots = ['cleanser', 'treatment', 'moisturizer'];
    var total = 0;
    var products = result.products.map(function (product, index) {
      if (!product || typeof product !== 'object' ||
        product.slot !== slots[index] || product.order !== index + 1 ||
        typeof product.handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,99}$/.test(product.handle) ||
        typeof product.title !== 'string' || !product.title.trim() ||
        typeof product.price !== 'number' || !Number.isFinite(product.price) || product.price < 0 ||
        !Number.isInteger(product.priceCents) || product.priceCents < 0 ||
        product.currency !== 'CAD' ||
        (product.availability !== 'available' && product.availability !== 'out_of_stock') ||
        typeof product.why !== 'string' || product.why.length > 100 ||
        typeof product.variantId !== 'string' || !/^\d{1,32}$/.test(product.variantId) ||
        (product.image !== undefined && typeof product.image !== 'string')) {
        throw safeError('invalid_routine_result');
      }
      total += product.priceCents;
      return {
        slot: product.slot,
        order: product.order,
        handle: product.handle,
        title: product.title,
        price: product.price,
        priceCents: product.priceCents,
        currency: product.currency,
        availability: product.availability,
        why: product.why,
        variantId: product.variantId,
        image: product.image || ''
      };
    });
    if (total !== result.totalPriceCents) throw safeError('invalid_routine_result');
    return {
      schemaVersion: result.schemaVersion,
      routineId: result.routineId,
      intro: result.intro,
      products: products,
      totalPriceCents: result.totalPriceCents,
      currency: result.currency,
      source: result.source,
      fallbackReason: result.fallbackReason
    };
  }

  function validateClarification(event) {
    var allowedKeys = { type: true, code: true, question: true, options: true };
    if (Object.keys(event).some(function (key) { return !allowedKeys[key]; }) ||
      event.code !== 'health_context' ||
      typeof event.question !== 'string' ||
      !event.question.trim() ||
      !Array.isArray(event.options) ||
      event.options.length !== 2) {
      throw safeError('invalid_clarification_event');
    }
    var seen = {};
    var options = event.options.map(function (option) {
      if (!option || typeof option !== 'object' ||
        Object.keys(option).some(function (key) { return key !== 'id' && key !== 'label'; }) ||
        (option.id !== 'personal_health' && option.id !== 'general_product') ||
        typeof option.label !== 'string' || !option.label.trim() ||
        seen[option.id]) {
        throw safeError('invalid_clarification_event');
      }
      seen[option.id] = true;
      return { id: option.id, label: option.label };
    });
    if (!seen.personal_health || !seen.general_product) throw safeError('invalid_clarification_event');
    return {
      code: event.code,
      question: event.question,
      options: options
    };
  }

  function validateEvent(event, state) {
    if (!event || typeof event !== 'object' || typeof event.type !== 'string') throw safeError('malformed_sse_event');
    if (state.done) throw safeError('sse_after_done');
    if (event.type === 'clarification') {
      if (state.clarification || state.fallback || state.text || state.products.length > 0) {
        throw safeError('invalid_clarification_order');
      }
      state.clarification = validateClarification(event);
    } else if (event.type === 'text') {
      if (state.fallback || state.clarification || typeof event.delta !== 'string') throw safeError('invalid_text_event');
      state.text += event.delta;
    } else if (event.type === 'product') {
      if (state.fallback || state.clarification ||
        typeof event.handle !== 'string' || !event.handle ||
        !state.text ||
        typeof event.title !== 'string' ||
        typeof event.role !== 'string' ||
        typeof event.why !== 'string' ||
        typeof event.price !== 'number' ||
        !Number.isFinite(event.price) || event.price < 0 ||
        state.products.length >= 8) {
        throw safeError('invalid_product_event');
      }
      state.products.push({
        handle: event.handle,
        title: event.title,
        price: event.price,
        role: event.role,
        why: event.why
      });
    } else if (event.type === 'source') {
      if (state.fallback || state.clarification || !state.text || state.sources.length >= 3) {
        throw safeError('invalid_source_event');
      }
      state.sources.push(validateSource(event));
    } else if (event.type === 'fallback') {
      if (state.fallback || state.clarification || state.text || state.products.length > 0) throw safeError('invalid_fallback_order');
      if (typeof event.reason !== 'string' || event.reason.length > 64) throw safeError('invalid_fallback_event');
      state.fallback = true;
      state.fallbackReason = event.reason;
      state.failureClass = failureClassForCode(event.reason);
    } else if (event.type === 'routine_result') {
      if (state.fallback || state.clarification || state.text || state.products.length > 0 || state.routineResult) {
        throw safeError('invalid_routine_result_order');
      }
      state.routineResult = validateRoutineResult(event.result);
    } else if (event.type === 'done') {
      state.done = true;
    } else {
      throw safeError('unknown_sse_event');
    }
    return event;
  }

  async function readAskSSE(response, onEvent, signal) {
    if (!response || !response.ok || !response.body || typeof response.body.getReader !== 'function') {
      var responseCode = 'ask_http_response';
      if (response && !response.ok && typeof response.clone === 'function') {
        try {
          var payload = await response.clone().json();
          var candidate = payload && payload.error && payload.error.code;
          if (isSafeHttpErrorCode(candidate)) responseCode = candidate;
        } catch (error) {}
      }
      throw safeError(responseCode);
    }
    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';
    var state = { done: false, fallback: false, fallbackReason: null, failureClass: null, text: '', products: [], sources: [], clarification: null, routineResult: null };

    function consume(block) {
      var event = parseDataBlock(block);
      if (event === null) return;
      var validated = validateEvent(event, state);
      if (typeof onEvent === 'function') onEvent(validated);
    }

    while (true) {
      if (signal && signal.aborted) throw safeError('cancelled');
      var chunk = await reader.read();
      buffer += decoder.decode(chunk.value || new Uint8Array(), { stream: !chunk.done });
      var parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() || '';
      parts.forEach(consume);
      if (chunk.done) break;
    }
    buffer += decoder.decode();
    if (buffer.trim()) consume(buffer);
    if (!state.done) throw safeError('incomplete_sse');
    if (!state.fallback && !state.text && !state.clarification && !state.routineResult) throw safeError('empty_sse_text');
    return state;
  }

  function combineSignal(controller, externalSignal) {
    if (!externalSignal) return;
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', function () { controller.abort(); }, { once: true });
  }

  function createAskRequestAdapter(config) {
    config = config || {};
    var fetcher = config.fetcher || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    var endpoint = String(config.endpoint || '');
    var timeoutMs = Number(config.timeoutMs || DEFAULT_TIMEOUT_MS);

    return {
      timeoutMs: timeoutMs,
      async send(input, options) {
        input = input || {};
        options = options || {};
        if (input.aiProcessingConsent !== true) return { blocked: 'consent_required', fallback: false, fallbackReason: null, failureClass: null, text: '', products: [], sources: [], clarification: null, routineResult: null };
        if (!fetcher || !endpoint) throw safeError('ask_endpoint_unavailable');
        var messages = validateMessages(input.messages);
        var turnstileToken = String(input.turnstileToken || '');
        if (!turnstileToken || turnstileToken.length > MAX_TURNSTILE_TOKEN_CHARACTERS) throw safeError('turnstile_required');

        var productContext = validateProductContext(input.productContext);
        var conversationId = validateConversationId(input.conversationId);
        var payload = {
          messages: messages,
          profile: input.profile || null,
          turnstile_token: turnstileToken,
          aiProcessingConsent: true
        };
        if (input.mode === 'find_my_routine') {
          payload.mode = 'find_my_routine';
          payload.routine_answers = {
            skin_type: input.routineAnswers && input.routineAnswers.skinType,
            sensitivity: input.routineAnswers && input.routineAnswers.sensitivity,
            goal: input.routineAnswers && input.routineAnswers.goal,
            safety: input.routineAnswers && input.routineAnswers.safety
          };
        }
        if (productContext && input.mode !== 'find_my_routine') payload.product_context = productContext;
        if (conversationId) payload.conversation_id = conversationId;
        var controller = new AbortController();
        combineSignal(controller, options.signal);
        var timer = setTimeout(function () { controller.abort(); }, timeoutMs);
        try {
          var response = await fetcher(endpoint, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal
          });
          var result = await readAskSSE(response, options.onEvent, controller.signal);
          return { blocked: null, fallback: result.fallback, fallbackReason: result.fallbackReason, failureClass: result.failureClass, text: result.text, products: result.products, sources: result.sources, clarification: result.clarification, routineResult: result.routineResult };
        } finally {
          clearTimeout(timer);
        }
      }
    };
  }

  return {
    MAX_MESSAGES: MAX_MESSAGES,
    MAX_MESSAGE_CHARACTERS: MAX_MESSAGE_CHARACTERS,
    DEFAULT_TIMEOUT_MS: DEFAULT_TIMEOUT_MS,
    createAskRequestAdapter: createAskRequestAdapter,
    readAskSSE: readAskSSE
  };
}));
